'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Box, Grid } from '@mui/material'
import { useWatch } from 'react-hook-form'

// Types
import type { FormProps } from '@/types/components/organisms/forms'

// Configuration
import APIConfig from '@/config/api'
import SystemConfig from '@/config/system'

// Components
const TextFieldMolecule = dynamic(() => import('@/components/molecules/field/text'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const SecondaryButtonAtom = dynamic(() => import('@/components/atoms/button/secondary'))
const TitleTypographyAtom = dynamic(() => import('@/components/atoms/typography/title'))
const ParagraphTypographyAtom = dynamic(() => import('@/components/atoms/typography/paragraph'))
const AlertItemAtom = dynamic(() => import('@/components/atoms/item/alert'))
const GmailIconAtom = dynamic(() => import('@/components/atoms/icon/colorful/gmail'))

const MobileFormOrganism = (props: FormProps) => {
	// Props
	const { lng, locale, control, formState, loading, onConfirm } = props

	// Variables
	const usernameValue: string = useWatch({ control, name: 'username' })
	const disabled = !usernameValue
	const errors = formState?.errors ?? {}

	return (
		<Grid
			component="form"
			container
			spacing={2}
			sx={{
				justifyContent: 'center',
				alignItems: 'center',
				flexDirection: 'column',
				width: 1,
				minHeight: 1,
				borderRadius: 3
			}}
		>
			{lng === 'fa' && (
				<Grid size={12}>
					<TitleTypographyAtom text={locale.common.title.loginRegister} textAlign="center" />
					<ParagraphTypographyAtom text={locale.common.description.inputYourMobile} textAlign="center" />
				</Grid>
			)}

			{lng === 'fa' && (
				<Grid size={12}>
					<TextFieldMolecule
						lng={lng}
						control={control}
						type="tel"
						name="username"
						pattern="mobile"
						label={locale.form.label.mobile}
						helperText={locale.form.helper.mobile}
						min={11}
						max={11}
						required
					/>
				</Grid>
			)}

			{lng === 'en' && (
				<Grid
					size={12}
					sx={{
						width: 1,
						'& a': {
							width: 1,
							'& .MuiButtonBase-root': { width: 1 }
						}
					}}
				>
					<Link href={`${APIConfig.appUrl}/user/google/redirect?redirect_to=site`}>
						<PrimaryButtonAtom text={locale.form.button.loginWithGmail} startIcon={<GmailIconAtom />} />
					</Link>
				</Grid>
			)}

			<Grid size={12}>
				<AlertItemAtom variant="standard" severity="info">
					<Box
						dangerouslySetInnerHTML={{ __html: locale.form.helper.login }}
						sx={{
							'& a': {
								color: 'secondary.main',
								textDecoration: 'inherit'
							}
						}}
					/>
				</AlertItemAtom>
			</Grid>

			{lng === 'fa' && (
				<Grid size={12} sx={{ '& .MuiButtonBase-root': { width: 1 } }}>
					<SecondaryButtonAtom
						text={locale.form.button.recieveConfirmationCode}
						type="submit"
						size="large"
						loading={loading}
						disabled={loading || disabled || Object.keys(errors).length > 0}
						onClick={onConfirm}
					/>
				</Grid>
			)}

			{!SystemConfig.isActive && (
				<Grid size={12} sx={{ '& .MuiButtonBase-root': { width: 1 } }}>
					<Link href={`/${lng}/login/username`}>
						<PrimaryButtonAtom text={locale.form.link.loginWithUsername} type="submit" size="large" />
					</Link>
				</Grid>
			)}
		</Grid>
	)
}

export default MobileFormOrganism
